import { Input } from "@/components/ui/input";
import { DateRange, FilterColumn, FilterFields, NumberRange } from ".";
import { Label } from "@radix-ui/react-label";
import { cn } from "@/lib/utils";
import { ComponentProps, useId } from "react";
import DateFilterField from "./fields/Date";
import NumberFilterField from "./fields/Number";
import BooleanFilterField from "./fields/Boolean";
import ComboBoxFilterField from "./fields/ComboBox";

type FilterFieldBaseProps = Omit<ComponentProps<typeof Input>, "onChange">;

interface FilterFieldProps<T> extends FilterFieldBaseProps {
    column: FilterColumn<T>;
    value: string;
    onChange: (value: string) => void;
    label?: string;
}

export default function FilterField<T>({
    column,
    value,
    onChange,
    placeholder = "Enter value..",
    label,
    ...rest
}: FilterFieldProps<T>) {
    const id = useId();

    if (FilterFields.date.includes(column.id as string)) {
        const dateValue: DateRange = (() => {
            try {
                const parsed = JSON.parse(value || '{"min":null,"max":null}');
                return {
                    min: parsed.min ? new Date(parsed.min) : null,
                    max: parsed.max ? new Date(parsed.max) : null,
                };
            } catch {
                return { min: null, max: null };
            }
        })();

        // Convert DateRange back to string
        const handleDateChange = (range: DateRange) => {
            onChange(JSON.stringify(range));
        };

        return (
            <DateFilterField
                column={column}
                value={dateValue}
                onChange={handleDateChange}
                placeholder={"Select Date"}
                {...rest}
            />
        );
    }

    if (FilterFields.number.includes(column.id as string)) {
        // Parse the string value to NumberRange
        const numberValue: NumberRange = (() => {
            try {
                return JSON.parse(value || '{"min":null,"max":null}');
            } catch {
                return { min: null, max: null };
            }
        })();

        // Convert NumberRange back to string
        const handleNumberChange = (range: NumberRange) => {
            onChange(JSON.stringify(range));
        };

        return (
            <NumberFilterField
                column={column}
                value={numberValue}
                onChange={handleNumberChange}
                placeholder={placeholder}
                {...rest}
            />
        );
    }

    if (FilterFields.boolean.includes(column.id as string)) {
        return (
            <BooleanFilterField
                column={column}
                value={value}
                onChange={onChange}
                placeholder="Select option..."
                className={rest.className}
                disabled={rest.disabled}
            />
        );
    }

    if (FilterFields.comboBox.includes(column.filterId as string)) {
        return (
            <ComboBoxFilterField
                column={column}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                label={label}
                {...rest}
            />
        );
    }

    return (
        <div className={cn("flex flex-col gap-2")}>
            <Label htmlFor={id} className={cn("text-sm font-medium")}>
                {column.headingText}
            </Label>
            <Input
                id={id}
                placeholder={placeholder}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                {...rest}
            />
        </div>
    );
}
